import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { environment } from '../../../../environments/environment';
import { RolesApiService } from '@angular-workspace/services';
import { ToasterService } from '../../../services/toaster.service';

export interface FooterLink {
  label: string;
  url: string;
  external?: boolean;
}

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FooterComponent {
  private readonly rolesService = inject(RolesApiService);
  private readonly toasterService = inject(ToasterService);
  private readonly router = inject(Router);
  private readonly keycloak = inject(KeycloakService);

  readonly currentYear = new Date().getFullYear();

  readonly logoSrc = '/iudx-health-logo-white.svg';
  readonly globeIconSrc = '/icons/world-white.svg';
  readonly healthcarePortalUrl = 'https://healthcare.iudx.io/';

  readonly brandTitle = 'IUDX Healthcare Data Exchange';
  readonly brandDescription =
    'IUDX Healthcare is a secure healthcare data exchange platform that supports providers, researchers, innovators and public health teams through governed discovery, responsible sharing and standards-led collaboration.';

  readonly importantLinksHeading = 'Important links';
  readonly developersHeading = 'For Developers';
  readonly partnersHeading = 'Partners';
  readonly contactUsLabel = 'Contact us';
  readonly privacyLabel = 'Privacy Policy';
  readonly termsLabel = 'Terms of Service';

  readonly importantLinks: FooterLink[] = [
    { label: 'About Us', url: '/about-us', external: false },
    { label: 'Datasets', url: '/datasets', external: false },
    { label: 'Models', url: '/models', external: false },
    { label: 'Usecases', url: '/usecases', external: false },
    { label: 'Sandbox', url: '/sandbox', external: false },
    { label: 'Leaderboard', url: '/leaderboard', external: false },
  ];

  readonly developerLinks: FooterLink[] = [
    { label: 'Control Plane API docs', url: environment.controlPlaneApiDocsUrl, external: true },
    { label: 'File Server API docs', url: environment.fileServerApiDocsUrl, external: true },
    { label: 'Resource Server API docs', url: environment.resourceServerApiDocsUrl, external: true },
  ];

  readonly partnerLogos: { name: string; src: string; url: string }[] = [
    { name: 'Indian Institute of Science', src: '/partner-logo/IISC-Logo-White.svg', url: '' },
    {
      name: 'ICMR NIE',
      src: '/partner-logo/icmr 1.svg',
      url: 'https://adarv.in/',
    },
    {
      name: 'Centre for Brain Research IISc',
      src: '/partner-logo/cbr_iisc_logo 1.svg',
      url: '',
    },
  ];

  onLogoError(event: Event): void {
    const el = event.target as HTMLImageElement | null;
    if (el) el.style.display = 'none';
  }

  private async checkLoginStatus(): Promise<boolean> {
    try {
      return await this.keycloak.isLoggedIn();
    } catch {
      return false;
    }
  }

  async onDashboardClick(event: Event): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    try {
      const isLoggedIn = await this.checkLoginStatus();
      if (!isLoggedIn) {
        this.toasterService.warning('You must be logged in to access the dashboard.');
        return;
      }

      await this.rolesService.getRoles();
      const userRoles = this.rolesService.getUserRoles();
      const route = this.rolesService.getHighestRolePanelRoute(userRoles || []);
      await this.router.navigate([route]);
    } catch {
      this.toasterService.warning('Unable to open dashboard. Please try again.');
    }
  }
}
